import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { journeyApi } from '../../api/journeyApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import SafeImage from '../../components/SafeImage';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui';
import { MY_JOURNEYS_PRODUCT_CHROME as chrome } from '../journey-workspace/journeyWorkspacePresentation';
import { normalizeMyJourneyList } from './myJourneysModel';
import './my-journeys.css';

export default function MyJourneysPage() {
  const { t } = useTravelTranslation();
  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [items, setItems] = useState([]);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    try {
      const payload = await journeyApi.listMine();
      if (requestId !== generation.current) return;
      setItems(normalizeMyJourneyList(payload));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      setErrorModel(mapApiErrorToUiModel(cause, { t }));
      setPhase('error');
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (phase === 'loading') {
    return (
      <main className="my-journeys-state" aria-live="polite">
        <LoadingState label={chrome.loading} data-testid="my-journeys-loading" />
      </main>
    );
  }

  if (phase === 'error') {
    const errorCopy = errorModel ? resolveUiErrorCopy(errorModel, t) : null;
    return (
      <main className="my-journeys-state" data-testid="my-journeys-error">
        <ErrorState
          title={errorCopy?.title || t('travel.common.apiError.unknown.title')}
          description={errorCopy?.description || t('travel.common.apiError.unknown.description')}
          retryable={Boolean(errorCopy?.retryable)}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  return (
    <main className="my-journeys" data-testid="my-trips-page" data-my-trips-ia="v2">
      <header className="my-journeys__header">
        <div>
          <p className="my-journeys__eyebrow">{chrome.eyebrow}</p>
          <h1>{chrome.title}</h1>
          <p className="my-journeys__lead">{chrome.lead}</p>
        </div>
        <div className="my-journeys__header-actions">
          <Link to="/invitations">旅程邀請</Link>
          <Link
            className="my-journeys__create"
            to="/journeys/new"
            data-testid="my-trips-create-shortcut"
            data-create-owner="GLOBAL_CREATE"
          >
            {chrome.create}
          </Link>
        </div>
      </header>

      {items.length === 0 ? (
        <EmptyState
          className="my-journeys-state my-journeys-state--empty"
          data-testid="my-journeys-empty"
          title={chrome.emptyTitle}
          description={chrome.emptyHint}
          action={(
            <Link
              className="my-journeys__create"
              to="/journeys/new"
              data-testid="my-trips-empty-create"
              data-create-owner="GLOBAL_CREATE"
            >
              {chrome.emptyCta}
            </Link>
          )}
        />
      ) : (
        <ul className="my-journeys__grid">
          {items.map((journey) => (
            <li key={journey.journeyId}>
              <Link className="my-journey-card" to={`/journeys/${journey.journeyId}`}>
                <SafeImage
                  className="my-journey-card__cover"
                  src={journey.coverUrl}
                  alt=""
                  fallback={<span className="my-journey-card__cover my-journey-card__cover--fallback" aria-hidden="true" />}
                />
                <span className="my-journey-card__body">
                  <strong>{journey.title}</strong>
                  {journey.destination ? <em>{journey.destination}</em> : null}
                  {journey.dateRange ? <span>{journey.dateRange}</span> : null}
                  <span className="my-journey-card__meta">
                    <span>{journey.lifecycleLabel}</span>
                    {journey.requesterRoleLabel ? <span>{journey.requesterRoleLabel}</span> : null}
                    {journey.updateTime ? (
                      <time dateTime={journey.updateTime}>
                        {chrome.updatedPrefix}
                        {' '}
                        {journey.updateTime}
                      </time>
                    ) : null}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
